/**
 * useActionPayloadSave - Shared hook for persisting edited action payloads.
 *
 * Every action card that supports inline editing follows the same save flow:
 *   1. Build an updated payload (caller-specific)
 *   2. POST it via ProposalClient.updateActionPayload
 *   3. Show a success toast
 *   4. Propagate the updated action to the parent via onUpdate
 *
 * This hook encapsulates steps 2–4, returning a stable `savePayload` callback
 * that callers pass their constructed payload to. The guard on
 * proposalId/orgId is handled once here instead of repeated in every card.
 */

import { useCallback } from 'react';
import { toast } from 'sonner';

import { createProposalClient } from '../../../lib/proposals/core/ProposalClient';
import type {
  ActionPayload,
  ProposedAction,
} from '../../../lib/proposals/core/types';

interface UseActionPayloadSaveOptions {
  orgId: number | undefined;
  proposalId: number | undefined;
  actionId: number;
  onUpdate?: (updatedAction: ProposedAction) => void;
  /**
   * Show the "Changes saved" toast (default: true).
   *
   * Set to false for autosaving editors: they fire a save per commit, so a
   * toast each time is noise. Those cards report progress with the inline
   * "Saving… / Saved / Failed to save" indicator instead — the same treatment
   * every other autosave editor in this tree uses.
   */
  showSuccessToast?: boolean;
}

/**
 * Returns a `savePayload` function that persists an updated action payload
 * and propagates the result to the parent.
 *
 * Throws if `proposalId` or `orgId` is missing at call time (same guard
 * that was previously inlined in every card's save callback).
 */
export function useActionPayloadSave({
  orgId,
  proposalId,
  actionId,
  onUpdate,
  showSuccessToast = true,
}: UseActionPayloadSaveOptions) {
  const savePayload = useCallback(
    async (updatedPayload: ActionPayload) => {
      if (!proposalId || !orgId) {
        throw new Error('proposalId and orgId are required to save');
      }
      try {
        const client = createProposalClient(orgId);
        const updatedAction = await client.updateActionPayload(
          proposalId,
          actionId,
          { payload: updatedPayload }
        );
        if (showSuccessToast) {
          toast.success('Changes saved');
        }
        onUpdate?.(updatedAction);
      } catch (error) {
        toast.error('Failed to save');
        throw error;
      }
    },
    [orgId, proposalId, actionId, onUpdate, showSuccessToast]
  );

  return { savePayload };
}
